import type { GithubPublishInput } from "@codvero/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowDownToLine, ArrowUpToLine, ExternalLink, GitMerge, GitPullRequest, Github, Unlink, X } from "lucide-react";
import { Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { githubApi, publishApi } from "@/api/imports.ts";
import { projectQuery } from "@/features/projects/ProjectPage.tsx";
import { cn } from "@/lib/cn.ts";
import { Alert, Button, Field, Input, Select, Spinner } from "@/ui/primitives.tsx";
import { useApiError } from "@/ui/useApiError.ts";
import { useConfirm } from "@/ui/ConfirmDialog.tsx";

/** Publish the workspace to GitHub (new or existing repository, direct or via pull request) and keep it in sync. */
export function GithubPublish({ projectId, canEdit }: { projectId: string; canEdit: boolean }) {
  const { t, i18n } = useTranslation();
  const project = useQuery(projectQuery(projectId)).data;
  const projectName = project?.name ?? "";
  const projectSlug = project?.slug ?? "project";
  const toMsg = useApiError();
  const qc = useQueryClient();
  const { confirm } = useConfirm();
  const st = useQuery({ queryKey: ["projects", projectId, "github"], queryFn: () => publishApi.status(projectId), refetchInterval: 30_000 });
  const refresh = (d?: unknown) => { if (d) qc.setQueryData(["projects", projectId, "github"], d); void qc.invalidateQueries({ queryKey: ["projects", projectId, "github"] }); void qc.invalidateQueries({ queryKey: ["git", projectId] }); };
  const [open, setOpen] = useState(false);
  const [msg, setMsg] = useState("");
  const push = useMutation({ mutationFn: () => publishApi.push(projectId, msg), onSuccess: (d) => { setMsg(""); refresh(d); } });
  const pull = useMutation({ mutationFn: () => publishApi.pull(projectId), onSuccess: refresh });
  const merge = useMutation({ mutationFn: () => publishApi.mergePr(projectId), onSuccess: refresh });
  const unlink = useMutation({ mutationFn: () => publishApi.unlink(projectId), onSuccess: () => refresh() });
  const d = st.data;
  const err = push.error ?? pull.error ?? merge.error ?? unlink.error;
  const fmt = (iso: string) => new Intl.DateTimeFormat(i18n.language, { dateStyle: "medium", timeStyle: "short" }).format(new Date(iso));
  if (!d) return null;
  return (
    <div className="border-b border-border px-2 py-2 text-xs">
      {!d.connected ? (
        <div className="flex items-center gap-2 text-fg-muted"><Github className="size-4" /> <span>{t("publish.notConnected")}</span> <Link to="/settings" hash="settings-agents" className="underline">{t("settings.tabs.accounts")}</Link></div>
      ) : !d.link ? (
        <div className="flex flex-wrap items-center gap-2">
          <Github className="size-4 text-fg-muted" />
          <span className="text-fg-muted">{t("publish.unpublished")}</span>
          {canEdit && <Button size="sm" className="ms-auto h-7 px-2 text-xs" onClick={() => setOpen(true)}><ArrowUpToLine className="size-3.5" /> {t("publish.open")}</Button>}
        </div>
      ) : (
        <div className="space-y-1.5">
          <div className="flex flex-wrap items-center gap-2">
            <Github className="size-4 text-fg-muted" />
            <a dir="ltr" href={d.link.htmlUrl} target="_blank" rel="noreferrer" className="font-mono underline">{d.link.fullName}</a>
            <span dir="ltr" className="rounded bg-surface-2 px-1 font-mono">{d.link.branch}</span>
            {d.link.how === "pull_request" && <span className="rounded bg-surface-2 px-1">{t("publish.viaPr")}</span>}
            {d.link.lastPushAt && <span className="text-fg-muted">{t("publish.lastPush", { when: fmt(d.link.lastPushAt) })}</span>}
            {canEdit && <button type="button" className="ms-auto rounded p-1 text-fg-muted hover:text-danger" title={t("publish.unlink")} onClick={async () => { if (await confirm({ title: t("publish.unlink"), message: t("publish.unlinkMsg"), confirmLabel: t("publish.unlink"), danger: true })) unlink.mutate(); }}><Unlink className="size-3.5" /></button>}
          </div>
          {d.link.pr && (
            <div className="flex flex-wrap items-center gap-2">
              <GitPullRequest className={cn("size-3.5", d.link.pr.state === "open" ? "text-success" : "text-fg-muted")} />
              <a dir="ltr" href={d.link.pr.url} target="_blank" rel="noreferrer" className="underline">#{d.link.pr.number}</a>
              <span className="text-fg-muted">{t(`publish.prState.${d.link.pr.state}`)}</span>
              {canEdit && d.link.pr.state === "open" && <Button size="sm" variant="secondary" className="h-7 px-2 text-xs" loading={merge.isPending} onClick={async () => { if (await confirm({ title: t("publish.merge"), message: t("publish.mergeMsg", { branch: d.link!.branch }), confirmLabel: t("publish.merge") })) merge.mutate(); }}><GitMerge className="size-3.5" /> {t("publish.merge")}</Button>}
            </div>
          )}
          {canEdit && (
            <form className="flex items-center gap-1.5" onSubmit={async (e) => { e.preventDefault(); if (d.link!.how === "direct" && !(await confirm({ title: t("publish.push"), message: t("publish.confirmDirect", { repo: d.link!.fullName, branch: d.link!.branch }), confirmLabel: t("publish.push") }))) return; push.mutate(); }}>
              <Input className="h-7 flex-1 text-xs" placeholder={d.git.dirty ? t("publish.pushPlaceholder") : t("publish.pushClean")} value={msg} onChange={(e) => setMsg(e.target.value)} />
              <Button type="submit" size="sm" className="h-7 px-2 text-xs" loading={push.isPending} title={t("publish.pushHint")}><ArrowUpToLine className="size-3.5" /> {t("publish.push")}</Button>
              <Button type="button" size="sm" variant="secondary" className="h-7 px-2 text-xs" loading={pull.isPending} title={t("publish.pullHint")} onClick={() => pull.mutate()}><ArrowDownToLine className="size-3.5" /> {t("publish.pull")}</Button>
            </form>
          )}
        </div>
      )}
      {err && <Alert tone="danger" className="mt-2">{toMsg(err)}</Alert>}
      {open && <PublishDialog projectId={projectId} projectName={projectName} projectSlug={projectSlug} onClose={() => setOpen(false)} onDone={(d) => { setOpen(false); refresh(d); }} />}
    </div>
  );
}

function PublishDialog({ projectId, projectName, projectSlug, onClose, onDone }: { projectId: string; projectName: string; projectSlug: string; onClose: () => void; onDone: (d: unknown) => void }) {
  const { t } = useTranslation();
  const toMsg = useApiError();
  const [mode, setMode] = useState<"new" | "existing">("new");
  const [name, setName] = useState(projectSlug);
  const [visibility, setVisibility] = useState<"public" | "private">("private");
  const [owner, setOwner] = useState<string>("");
  const [description, setDescription] = useState("");
  const [q, setQ] = useState("");
  const [qDebounced, setQDebounced] = useState("");
  useEffect(() => { const id = setTimeout(() => setQDebounced(q), 350); return () => clearTimeout(id); }, [q]);
  const [fullName, setFullName] = useState("");
  const [branch, setBranch] = useState("");
  const [how, setHow] = useState<"direct" | "pull_request">("pull_request");
  const orgs = useQuery({ queryKey: ["github", "orgs"], queryFn: githubApi.orgs, staleTime: 5 * 60_000 });
  const repos = useQuery({ queryKey: ["github", "repos", qDebounced], queryFn: () => githubApi.repos(qDebounced), enabled: mode === "existing", staleTime: 60_000 });
  const gh = useQuery({ queryKey: ["github", "status"], queryFn: githubApi.status });
  const publish = useMutation({ mutationFn: (input: GithubPublishInput) => publishApi.publish(projectId, input), onSuccess: onDone });
  const selected = repos.data?.find((r) => r.fullName === fullName) ?? null;
  const submit = () => {
    if (mode === "new") publish.mutate({ mode: "new", name: name.trim(), visibility, owner: owner || null, description: description.trim() });
    else if (selected) publish.mutate({ mode: "existing", fullName: selected.fullName, branch: (branch || selected.defaultBranch).trim(), how });
  };
  const canSubmit = mode === "new" ? /^[A-Za-z0-9._-]+$/.test(name.trim()) : !!selected;
  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/50 p-4" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="w-full max-w-lg space-y-3 rounded-xl border border-border bg-surface p-4 text-sm shadow-xl" role="dialog" aria-label={t("publish.title")}>
        <div className="flex items-center justify-between">
          <h3 className="flex items-center gap-2 font-semibold"><Github className="size-4" /> {t("publish.title")} <span className="text-xs font-normal text-fg-muted">· {projectName}</span></h3>
          <button type="button" className="rounded p-1 text-fg-muted hover:text-fg" onClick={onClose} aria-label={t("common.close")}><X className="size-4" /></button>
        </div>
        <div className="grid grid-cols-2 gap-1 rounded-lg border border-border p-1 text-xs">
          {(["new", "existing"] as const).map((m) => <button key={m} type="button" className={cn("rounded-md px-2 py-1.5", mode === m ? "bg-accent-soft text-accent" : "text-fg-muted hover:text-fg")} onClick={() => setMode(m)}>{t(`publish.mode.${m}`)}</button>)}
        </div>
        {mode === "new" ? (
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label={t("publish.owner")}>
              <Select value={owner} onChange={(e) => setOwner(e.target.value)}>
                <option value="">{gh.data?.login ?? t("publish.me")}</option>
                {(orgs.data ?? []).map((o) => <option key={o.login} value={o.login}>{o.login}</option>)}
              </Select>
            </Field>
            <Field label={t("publish.name")}><Input dir="ltr" className="font-mono" value={name} onChange={(e) => setName(e.target.value)} /></Field>
            <Field label={t("publish.visibility")}>
              <div className="grid grid-cols-2 gap-1 rounded-lg border border-border p-1 text-xs">
                {(["private", "public"] as const).map((v) => <button key={v} type="button" className={cn("rounded-md px-2 py-1.5", visibility === v ? "bg-accent-soft text-accent" : "text-fg-muted hover:text-fg")} onClick={() => setVisibility(v)}>{t(`publish.vis.${v}`)}</button>)}
              </div>
            </Field>
            <Field label={t("publish.description")}><Input value={description} maxLength={300} onChange={(e) => setDescription(e.target.value)} /></Field>
            <p className="text-xs text-fg-muted sm:col-span-2">{t("publish.newHint")}</p>
          </div>
        ) : (
          <div className="space-y-3">
            <Input dir="ltr" placeholder={t("publish.search")} value={q} onChange={(e) => setQ(e.target.value)} />
            <div className="max-h-48 overflow-auto rounded-lg border border-border">
              {repos.isLoading && <div className="p-3"><Spinner /></div>}
              {repos.data?.length === 0 && <p className="p-3 text-xs text-fg-muted">{t("publish.noRepos")}</p>}
              {(repos.data ?? []).map((r) => (
                <button key={r.fullName} type="button" onClick={() => { setFullName(r.fullName); setBranch(r.defaultBranch); }} className={cn("flex w-full items-center gap-2 border-b border-border px-3 py-2 text-start last:border-0 hover:bg-surface-2", fullName === r.fullName && "bg-accent-soft/40")}>
                  <span dir="ltr" className="min-w-0 flex-1 truncate font-mono text-xs">{r.fullName}</span>
                  <span className="text-[10px] text-fg-muted">{r.private ? t("publish.vis.private") : t("publish.vis.public")} · {r.defaultBranch}</span>
                </button>
              ))}
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <Field label={t("publish.branch")}><Input dir="ltr" className="font-mono" value={branch} placeholder={selected?.defaultBranch ?? "main"} onChange={(e) => setBranch(e.target.value)} /></Field>
              <Field label={t("publish.how")}>
                <Select value={how} onChange={(e) => setHow(e.target.value as "direct" | "pull_request")}>
                  <option value="pull_request">{t("publish.howPr")}</option>
                  <option value="direct">{t("publish.howDirect")}</option>
                </Select>
              </Field>
            </div>
            <p className="text-xs text-fg-muted">{how === "pull_request" ? t("publish.prHint") : t("publish.directHint")}</p>
          </div>
        )}
        {publish.error && <Alert tone="danger">{toMsg(publish.error)}</Alert>}
        <div className="flex items-center justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>{t("common.cancel")}</Button>
          <Button disabled={!canSubmit} loading={publish.isPending} onClick={submit}><ExternalLink className="size-4" /> {t("publish.submit")}</Button>
        </div>
      </div>
    </div>
  );
}
